import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useMenuModalStates } from "../../../hooks/use-menu-modal-states";
import MenuModal from "../../../components/tools/menu-modal";
import AppointmentStatusHistoryModal, {
  PharmacyOrderStatusHistoryModal,
} from "../../../components/appointment-status-history/appointment-status-history-modal";

import api from "../../../API/api";
import {
  Badge,
  IconButton,
  MenuItem,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CustomizedMenus from "../../../components/tools/menu";
import useDebounce from "../../../hooks/use-debounce";
import TextFieldTableHead from "../../../components/table/textfield-table-head";
import DebounceSpinner from "../../../components/common/debounce-spinner";
import AccordionFilter from "../../../components/filter/accordion-filter";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#016b8b',
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const PharmacyOrders = () => {
  const tableRef = useRef(null);
  const [getRow, setGetRow] = useState();
  const [orders, setOrders] = useState();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [filters, setFilters] = useState({
    filter_status: "",
  });

  const viewOrders = (newPage) => {
    api
      .GetPharmacyOrders({ page: newPage + 1, per_page: rowsPerPage }, 1)  // Convert 0-based MUI page to 1-based API page
      .then((res) => {
        console.log("===>>>>", res);
        setOrders(res.data.pharmacy);
      })
      .catch((err) => {
        console.log(err.response);
      });
  };

  useEffect(() => {
    viewOrders();
  }, []);

  // Fetch data when page or rowsPerPage changes
  useEffect(() => {
    viewOrders(page);
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    viewOrders(0, event.target.value);
  };

  const handleStatus = (status, remarks) => {
    setUpdatingStatus(true);
    const payload = {
      pharmacy_order_id: getRow.id,
      status: status,
      remarks,
    };
    api
      .updatePharmacyOrderStatus(payload)
      .then((res) => {
        console.log(res);
        viewOrders();
        handleCloseModal();
        if (res.data.success) {
          Swal.fire({
            title: "Success!",
            text: "Status Updated",
            icon: "success",
            confirmButtonText: "Ok",
          });
          toast.success("Status Updated Successfully");
        }
      })
      .catch((err) => {
        if (err.response.data.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error("Something went wrong!");
        }
      })
      .finally(() => {
        setUpdatingStatus(false);
        setModalProps({});
      });
  };

  const menuButtons = [
    {
      id: 1,
      name: "Confirmed",
      onClick: (remarks) => handleStatus("confirmed", remarks),
    },
    {
      id: 2,
      name: "Cancelled",
      onClick: (remarks) => handleStatus("cancelled", remarks),
    },
    {
      id: 3,
      name: "Completed",
      onClick: (remarks) => handleStatus("completed", remarks),
    },
    {
      id: 4,
      name: "Unable to reach",
      onClick: (remarks) => handleStatus("Unable_to_reach", remarks),
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "requested":
        return "primary";
      case "confirmed":
        return "success";
      case "completed":
        return "warning";
      case "cancelled":
        return "error";
      case "Unable_to_reach":
        return "info";
      default:
        return "default";
    }
  };

  const viewOrdersByFilters = () => {
    setPage(0);
    setRowsPerPage(5);

    let newFilters = { ...filters };
    Object.keys(newFilters).forEach((key) => {
      if (newFilters[key] === "") {
        delete newFilters[key];
      }
    });
    api
      .GetPharmacyOrders({ page: 1, per_page: 5 }, 1, null, newFilters)
      .then((res) => {
        console.log("===>>>>", res);
        setOrders(res.data.pharmacy);
      })
      .catch((err) => {
        console.log(err.response);
      });
  };

  const [debouncedViewOrdersByFilter, isDebouncing] = useDebounce(
    viewOrdersByFilters,
    300
  );

  useEffect(() => {
    debouncedViewOrdersByFilter();
  }, [filters]);

  const {
    openModal,
    modalProps,
    updatingStatus,
    setUpdatingStatus,
    statusHistoryModal,
    statusHistoryModalData,
    setStatusHistoryModalData,
    handleOpenStatusHistoryModal,
    handleCloseStatusHistoryModal,
    setModalProps,
    handleOpenModal,
    handleCloseModal,
  } = useMenuModalStates();

  const handleStatusBadgeClick = (row) => {
    setGetRow(row);
    setStatusHistoryModalData(row);
    handleOpenStatusHistoryModal();
  };

  return (
    <>
      <div className="row justify-content-between">
        <div className="col-6">
          <h4>Pharmacy </h4>
        </div>
      </div>
      <AccordionFilter
        filters={filters}
        setFilters={setFilters}
        filterOptions={{
          from: {
            label: "From",
            type: "date",
          },
          to: {
            label: "To",
            type: "date",
          },
        }}
        onFilterChange={debouncedViewOrdersByFilter}
      />
      <TableContainer component={Paper} ref={tableRef}>
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell>ID</StyledTableCell>
              <StyledTableCell>Created At</StyledTableCell>
              <StyledTableCell align="center">
                <TextFieldTableHead
                  title={"Name"}
                  type={"text"}
                  onChange={(e) => {
                    setFilters({ ...filters, name: e.target.value });
                  }}
                />
              </StyledTableCell>
              <StyledTableCell align="center">Phone</StyledTableCell>
              <StyledTableCell align="center">Email</StyledTableCell>
              <StyledTableCell align="center">Prescription</StyledTableCell>
              <StyledTableCell align="center">Delivery Address</StyledTableCell>
              <StyledTableCell align="center">Last Updated By</StyledTableCell>
              <StyledTableCell align="center">
                <TextField
                  select
                  label="Status"
                  value={filters.filter_status}
                  onChange={(e) =>
                    setFilters({ ...filters, filter_status: e.target.value })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "white",
                      },
                      "&:hover fieldset": {
                        borderColor: "white",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "white",
                      },
                      color: "white", // Text color for the select
                    },
                    "& .MuiInputLabel-root": {
                      color: "white",
                    },
                    "& .MuiSelect-root": {
                      color: "white",
                    },
                    "& .MuiSelect-icon": {
                      color: "white", // Dropdown icon color
                    },
                    "& .MuiMenuItem-root": {
                      color: "white", // Text color for menu items
                    },
                    minWidth: 150,
                    color: "white", // Text color for the TextField
                  }}
                  InputLabelProps={{
                    style: { color: "white" }, // Ensure label is white even when focused
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          bgcolor: "rgba(0, 0, 0, 0.8)", // Dark background for dropdown
                          "& .MuiMenuItem-root": {
                            color: "white", // Ensure menu items are white
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="requested">Requested</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
              </StyledTableCell>
              <StyledTableCell align="center">Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isDebouncing && <DebounceSpinner isDebouncing={isDebouncing} />}
            {!isDebouncing &&
              orders &&
              orders?.data?.map((row, index) => (
                <StyledTableRow key={row.id}>
                  <StyledTableCell component="th" scope="row">
                    {index + 1}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {new Date(row.created_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </StyledTableCell>
                  <StyledTableCell align="center">{row.name}</StyledTableCell>
                  <StyledTableCell align="center">{row.phone}</StyledTableCell>
                  <StyledTableCell align="center">{row.email}</StyledTableCell>
                  <StyledTableCell align="center">
                    <a
                      href={row.prescription}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Prescription
                    </a>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {row.delivery_address}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {row.last_updated_by && row.last_updated_by.length > 0 ? (
                      <Stack direction={"column"}>
                        <Typography variant="body2" color="text.secondary">
                          {/* <img  height="40px" src={`${process.env.REACT_APP_BACKEND_USER}/${row?.last_updated_by[0].id}/${row?.last_updated_by[0].image}`} /> */}
                          {
                            row.last_updated_by.length > 0 ?
                              <img height="40px" src={`${process.env.REACT_APP_BACKEND_USER}/${row?.last_updated_by[row.last_updated_by.length - 1].id}/${row?.last_updated_by[row.last_updated_by.length - 1].image}`} />
                              :
                              <img height="40px" src={`${process.env.REACT_APP_BACKEND_USER}/${row?.last_updated_by[0].id}/${row?.last_updated_by[0].image}`} />
                          }
                          {
                            row.last_updated_by.length > 0 ?
                              row.last_updated_by[row.last_updated_by.length - 1].name
                              :
                              row.last_updated_by[0].name

                          }
                        </Typography>
                        <Typography variant="body2">
                          {
                            row.last_updated_by.length > 0 ?
                              row.last_updated_by[row.last_updated_by.length - 1].email
                              :
                              row.last_updated_by[0].email

                          }
                        </Typography>
                        <Typography variant="body2">
                          {row.last_updated_by[0].email}
                        </Typography>
                      </Stack>
                    ) : (
                      "null"
                    )}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Tooltip title="Click to view the history" arrow>
                      <IconButton onClick={() => handleStatusBadgeClick(row)}>
                        <Badge
                          badgeContent={row.status}
                          color={getStatusColor(row.status)}
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <CustomizedMenus
                      opensModal={true}
                      setModalProps={setModalProps}
                      handleOpenModal={handleOpenModal}
                      openModal={openModal}
                      row={row}
                      menuButtons={menuButtons}
                      setGetRow={setGetRow}
                      btnText={"update status"}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={orders?.total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <MenuModal
        updatingStatus={updatingStatus}
        handleOpen={handleOpenModal}
        handleClose={handleCloseModal}
        open={openModal}
        status={modalProps.status}
        handleClick={modalProps.onClick}
        setModalProps={setModalProps}
      />
      <PharmacyOrderStatusHistoryModal
        open={statusHistoryModal}
        handleClose={handleCloseStatusHistoryModal}
        data={statusHistoryModalData}
        setStatusHistoryModalData={setStatusHistoryModalData}
        getAppointmentHistoryFromServer={api.pharmacyOrderStatusRecords}
        historyFor="pharmacy_orders"
      />
    </>
  );
};

export default PharmacyOrders;
